export type PublicNavLink = {
  label: string;
  to: string;
};

const home: PublicNavLink = { label: "Home", to: "/" };
const project: PublicNavLink = { label: "The Project", to: "/about-project" };
const events: PublicNavLink = { label: "Events", to: "/events" };
const supportUs: PublicNavLink = { label: "Support Us", to: "/support-us" };
const csa: PublicNavLink = { label: "CSA", to: "/csa" };
const joinUs: PublicNavLink = { label: "Join Us!", to: "/join" };
const education: PublicNavLink = { label: "Education", to: "/education" };
const gallery: PublicNavLink = { label: "Gallery", to: "/gallery" };
const memberships: PublicNavLink = { label: "Memberships", to: "/memberships" };
const publications: PublicNavLink = {
  label: "Publications",
  to: "/publications",
};
const wishlist: PublicNavLink = { label: "Wishlist", to: "/wishlist" };
const sponsors: PublicNavLink = { label: "Sponsors", to: "/sponsors" };

/** Jan 1 – Jun 1: CSA in the main bar; Jun 2 – Dec 31: Join Us in the main bar. */
export function isCsaPrimaryNavSeason(date: Date = new Date()): boolean {
  const month = date.getMonth() + 1;
  const day = date.getDate();

  if (month < 6) return true;
  if (month === 6 && day === 1) return true;
  return false;
}

const DEV_NAV_SEASON_STORAGE_KEY = "dev-nav-season";

/**
 * Dev-only nav season preview. Ignored in production builds.
 *
 * - `?navSeason=csa` — Jan 1–Jun 1 layout (CSA in main bar)
 * - `?navSeason=join` — Jun 2–Dec 31 layout (Join Us in main bar)
 * - `?navDate=2025-03-01` — simulate a specific calendar day
 * - `localStorage.setItem("dev-nav-season", "csa" | "join")` — persists until cleared
 */
export function getDevNavDateOverride(): Date | null {
  if (process.env.NODE_ENV !== "development" || typeof window === "undefined") {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  const navDate = params.get("navDate");
  if (navDate) {
    const [year, month, day] = navDate.split("-").map(Number);
    if (year && month && day) {
      return new Date(year, month - 1, day);
    }
  }

  const navSeason =
    params.get("navSeason") ??
    window.localStorage.getItem(DEV_NAV_SEASON_STORAGE_KEY);
  if (navSeason === "csa") return new Date(2025, 2, 1);
  if (navSeason === "join") return new Date(2025, 6, 15);

  return null;
}

export function resolvePublicNavDate(fallback: Date = new Date()): Date {
  return getDevNavDateOverride() ?? fallback;
}

export function getPublicNavLayout(date: Date = resolvePublicNavDate()) {
  const csaPrimary = isCsaPrimaryNavSeason(date);

  const mainLinks: PublicNavLink[] = csaPrimary
    ? [home, project, events, supportUs, csa]
    : [home, project, events, supportUs, joinUs];

  const moreLinks: PublicNavLink[] = csaPrimary
    ? [
        joinUs,
        education,
        gallery,
        memberships,
        publications,
        wishlist,
        sponsors,
      ]
    : [education, gallery, csa, memberships, publications, wishlist, sponsors];

  const hamburgerLinks: PublicNavLink[] = [...mainLinks, ...moreLinks];

  return { mainLinks, moreLinks, hamburgerLinks };
}

export function isActiveNavPath(pathname: string, to: string): boolean {
  if (to === "/") return pathname === "/";
  return pathname === to || pathname.startsWith(`${to}/`);
}
